import { Config } from "../.env.js";

// Keeps a signed-in session alive while the user is working. Session tokens
// have a fixed lifetime and nothing used to renew them, so they ran out
// mid-session and the next authenticated call (or the sidebar's session
// poll) signed the user out. On user activity, once the stored token is
// over a day old, it is swapped for a fresh 30-day one from the auth
// backend - so a session ends ~30 days after the user's last activity, for
// every role. An idle tab never refreshes, so an abandoned session still
// ends.
//
// The same file lives in the auth, influencer and community frontends -
// keep them in sync.

const REFRESH_URL = `${Config.API_URL}/api/session/refresh`;
const ACTIVITY_CHECK_THROTTLE_MS = 60 * 1000;
const REFRESH_AFTER_SECONDS = 24 * 60 * 60;
const ACTIVITY_EVENTS = ["click", "keydown"];

// Shown on the sign-in page after a session ends on its own (read by the
// auth app's App.js). sessionStorage survives the redirect, not the tab.
export const SESSION_ENDED_MESSAGE_KEY = "sessionEndedMessage";
export const SESSION_EXPIRED_MESSAGE =
  "Your session has expired. Please sign in again.";

const decodeToken = (token) => {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(payload));
  } catch (err) {
    return null;
  }
};

// Tokens without an `exp` claim never expire and count as not expired.
export const isTokenExpired = (token) => {
  const payload = token ? decodeToken(token) : null;
  return Boolean(payload?.exp) && payload.exp * 1000 <= Date.now();
};

// The internal sign-in page only opens with the internal secret in the URL
// (InternalGuard) - without it the user is bounced to the marketing home
// page, which looked like a random logout.
export const getSigninUrl = (signinPath = "/internal/signin") => {
  const secret = process.env.REACT_APP_INTERNAL_SECRET;
  const query =
    signinPath === "/internal/signin" && secret
      ? `?token=${encodeURIComponent(secret)}`
      : "";
  return `${Config.SITE_URL}${signinPath}${query}`;
};

// Ends the session from the client side with a message for the sign-in page
// instead of dropping the user there without explanation.
export const endSession = (message = SESSION_EXPIRED_MESSAGE, signinPath) => {
  try {
    sessionStorage.setItem(SESSION_ENDED_MESSAGE_KEY, message);
  } catch (err) {
    // Storage blocked - the redirect still works, just without the notice.
  }
  localStorage.clear();
  window.location.href = getSigninUrl(signinPath);
};

let inFlight = null;

export const refreshSessionIfNeeded = () => {
  if (inFlight) return inFlight;

  const token = localStorage.getItem("token");
  const payload = token ? decodeToken(token) : null;
  if (!payload?.exp || !payload?.iat || payload.purpose) return null;

  const nowSeconds = Date.now() / 1000;
  // Already expired: nothing to refresh. Issued recently with plenty left:
  // nothing to do. (A short-lived token from before the 30-day sessions
  // never gets a day old, so it is renewed once it has under a day left.)
  if (
    payload.exp <= nowSeconds ||
    (nowSeconds - payload.iat < REFRESH_AFTER_SECONDS &&
      payload.exp - nowSeconds > REFRESH_AFTER_SECONDS)
  ) {
    return null;
  }

  inFlight = (async () => {
    try {
      const res = await fetch(REFRESH_URL, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json().catch(() => null);
      // Another tab may have refreshed already, or the user signed in as
      // someone else meanwhile - only replace the token we refreshed.
      if (data?.token && localStorage.getItem("token") === token) {
        localStorage.setItem("token", data.token);
      }
    } catch (err) {
      // Network hiccup - the next activity check retries.
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
};

// Call once from App. Returns a cleanup function.
export const startSessionKeeper = () => {
  let lastCheck = 0;
  const check = () => {
    const now = Date.now();
    if (now - lastCheck < ACTIVITY_CHECK_THROTTLE_MS) return;
    lastCheck = now;
    refreshSessionIfNeeded();
  };
  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") check();
  };

  ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, check));
  document.addEventListener("visibilitychange", onVisibilityChange);
  check();

  return () => {
    ACTIVITY_EVENTS.forEach((event) =>
      window.removeEventListener(event, check)
    );
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
};
